// src/components/Header.jsx
import React, { useState, useRef, useEffect } from "react";
import { FaUserCircle, FaSearch, FaBell } from "react-icons/fa";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import logo from "../assets/logo.jpg";

const Header = () => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [message, setMessage] = useState("");
    const [email, setEmail] = useState("User");
    const dropdownRef = useRef();

    useEffect(() => {
        const token = localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setEmail(decoded.email || "User");
            } catch { /* ignore invalid token */ }
        }
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.reload();
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        if (newPassword !== confirmPassword) {
            setMessage("New passwords do not match.");
            return;
        }
        try {
            const { data } = await axios.post(
                `${process.env.REACT_APP_API_URL}/change-password`,
                { email, oldPassword, newPassword }
            );
            if (data.success) {
                setMessage("Password changed successfully.");
                setTimeout(handleLogout, 1500);
            } else {
                setMessage(data.message || "Something went wrong.");
            }
        } catch (err) {
            setMessage(err.response?.data?.message || "Server error");
        }
    };

    useEffect(() => {
        const onClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    const comingSoon = (feature) => {
        window.alert(`${feature} coming soon!`);
    };

    return (
        <header className="fixed top-0 left-0 right-0 z-40 flex h-16">
            {/* Logo + title over the sidebar */}
            <div className="w-64 bg-gray-900 flex items-center gap-3 px-6">
                <img
                    src={logo}
                    alt="AutoRAT Logo"
                    className="h-8 w-8 rounded-sm shadow-sm"
                />
                <span className="text-white text-lg font-semibold">AutoRAT Portal</span>
            </div>

            {/* White header over content */}
            <div className="flex-1 bg-white shadow-sm border-b border-gray-200 flex items-center justify-end px-6 gap-6">
                {/* Username + dropdown */}
                <div className="relative" ref={dropdownRef}>
                    <button
                        onClick={() => setShowDropdown((p) => !p)}
                        className="flex items-center gap-2 focus:outline-none"
                    >
                        <FaUserCircle size={24} className="text-gray-600" />
                        <span className="text-sm text-gray-700 hover:text-gray-900 transition">
              {email}
            </span>
                    </button>
                    {showDropdown && (
                        <div className="absolute right-0 mt-2 w-48 bg-white text-gray-800 rounded-md shadow-lg z-50 overflow-hidden">
                            <button
                                className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm"
                                onClick={() => {
                                    setShowChangePasswordModal(true);
                                    setShowDropdown(false);
                                }}
                            >
                                Change Password
                            </button>
                            <button
                                onClick={handleLogout}
                                className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 text-sm"
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </div>

                {/* Search icon */}
                <button
                    onClick={() => comingSoon("Search")}
                    className="p-2 rounded-full hover:bg-gray-100"
                >
                    <FaSearch className="text-gray-600" />
                </button>

                {/* Notification icon */}
                <button
                    onClick={() => comingSoon("Notifications")}
                    className="p-2 rounded-full hover:bg-gray-100 relative"
                >
                    <FaBell className="text-gray-600" />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1">
            3
          </span>
                </button>

                {/* Optional version badge */}
                <span className="text-xs text-gray-400">v1.0.0</span>
            </div>

            {/* Change Password Modal */}
            {showChangePasswordModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-96 shadow-xl">
                        <h2 className="text-xl font-semibold mb-4 text-gray-800 text-center">
                            Change Password
                        </h2>
                        {message && (
                            <p className="text-center text-sm text-red-500 mb-2">{message}</p>
                        )}
                        <form onSubmit={handleChangePassword} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Old Password
                                </label>
                                <input
                                    type="password"
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    required
                                    className="w-full border rounded-md px-3 py-2 mt-1 text-sm focus:ring-2 focus:ring-purple-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    New Password
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    className="w-full border rounded-md px-3 py-2 mt-1 text-sm focus:ring-2 focus:ring-purple-400"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Confirm New Password
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    className="w-full border rounded-md px-3 py-2 mt-1 text-sm focus:ring-2 focus:ring-purple-400"
                                />
                            </div>
                            <div className="flex justify-end gap-2 mt-4">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowChangePasswordModal(false);
                                        setMessage("");
                                        setOldPassword("");
                                        setNewPassword("");
                                        setConfirmPassword("");
                                    }}
                                    className="px-4 py-2 rounded-md border text-gray-600 hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                                >
                                    Change
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
